import React from 'react';
import { getBadgeColor, getAchievementRarity } from '../../config/badgeColors';
import AchievementImage from './AchievementImage';
import './AchievementDetailModal.scss';

const AchievementDetailModal = ({ achievement, isOpen, onClose, isPublicView = false }) => {


  const getProgressInfo = () => {
    if (!achievement) return null;

    if (achievement.category === 'daily-streak') {
      const currentStreak = achievement.currentDailyStreak || 0;
      const required = achievement.threshold;
      return {
        current: currentStreak,
        required: required,
        percentage: Math.min((currentStreak / required) * 100, 100),
        label: `Current daily streak: ${currentStreak}/${required} days`
      };
    } else if (achievement.game_type) {
      const currentWins = achievement.currentWins || 0;
      const required = achievement.threshold;
      return {
        current: currentWins,
        required: required,
        percentage: Math.min((currentWins / required) * 100, 100),
        label: `Current wins: ${currentWins}/${required}`
      };
    }
    return null;
  };

  const formatUnlockDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('Invalid date string:', dateString);
        return 'Unknown date';
      }
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return 'Unknown date';
    }
  };

  if (!isOpen || !achievement) return null;

  const progressInfo = getProgressInfo();
  const rarity = getAchievementRarity(achievement);
  const badgeColor = getBadgeColor(achievement);

  const handleOverlayClick = (e) => {
    // Check if the click is on the overlay itself (and not its children)
    if (e.target === e.currentTarget) {
      // Stop the click from bubbling up to the parent card, which would re-open the modal
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div className="achievement-modal-overlay" onClick={handleOverlayClick}>
      <div className="achievement-modal" onClick={(e) => e.stopPropagation()}>
        <button className="achievement-modal__close" onClick={onClose}>×</button>
        
        <div className="achievement-modal__content">
          <div className="achievement-modal__image-section">
            <div className="achievement-modal__image-container">
              <AchievementImage
                filename={achievement.image_filename}
                alt={achievement.name}
                className="achievement-modal__image"
              />
              
              {!achievement.unlocked && (
                <div className="achievement-modal__status-badge">
                  🔒
                </div>
              )}
            </div>
          </div>

          <div className="achievement-modal__info-section">
            <h2 className="achievement-modal__title">{achievement.name}</h2>
            
            <div className="achievement-modal__rarity-container">
              <div 
                className={`achievement-modal__rarity achievement-modal__rarity--${rarity}`}
                style={{
                  '--rarity-color': badgeColor.background,
                  '--rarity-glow': badgeColor.glow,
                  '--rarity-border': badgeColor.borderColor || badgeColor.background
                }}
              >
                <span className="achievement-modal__rarity-icon">
                  ★
                </span>
                <span className="achievement-modal__rarity-text">
                  {rarity.charAt(0).toUpperCase() + rarity.slice(1)}
                </span>
              </div>
            </div>
            
            <p className="achievement-modal__description">{achievement.description}</p>

            <div className="achievement-modal__unlock-status">
              {achievement.unlocked ? (
                <div className="achievement-modal__unlocked">
                  <span className="achievement-modal__unlocked-text">
                    Unlocked on {formatUnlockDate(achievement.unlocked_date)}
                  </span>
                </div>
              ) : (
                <div className="achievement-modal__locked">
                  <span className="achievement-modal__locked-text">Not yet unlocked</span>
                  
                  {progressInfo && !isPublicView && (
                    <div className="achievement-modal__progress">
                      <div className="achievement-modal__progress-label">
                        {progressInfo.label}
                      </div>
                      <div className="achievement-modal__progress-bar">
                        <div 
                          className="achievement-modal__progress-fill"
                          style={{ width: `${progressInfo.percentage}%` }}
                        ></div>
                      </div>
                      <div className="achievement-modal__progress-percentage">
                        {Math.round(progressInfo.percentage)}%
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
        
        {achievement?.globalStats && (
          <div className="achievement-modal__player-count">
            {achievement.globalStats.unlockedCount === 1 
              ? "1 player has this achievement"
              : `${achievement.globalStats.unlockedCount} players have this achievement`
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default AchievementDetailModal;